import { useEChart } from './useEChart.js';
import { ChartFrame } from './ChartFrame.js';
import { formatChartAmount } from './formatters.js';

import type { ChartBaseStateProps } from './types.js';

/** Mirrors AgingResponse['buckets'] in apps/client/src/lib/types/finance.ts —
 * from the real GET .../finance/{ar|ap}/aging (packages/module-accounting's
 * queryAging), never computed client-side. */
export interface AgingBucketDatum {
  readonly bucket: string;
  readonly label: string;
  readonly openBalanceRial: number;
}

export interface AgingBucketsChartProps extends ChartBaseStateProps {
  readonly buckets: readonly AgingBucketDatum[];
  readonly ariaLabel: string;
  readonly height?: number | undefined;
  /** Overrides the default "pick a base date" copy — callers that always
   * pass a fixed as-of date (e.g. the Finance Hub's "today") have nothing
   * left to pick, so the default message would be misleading there. */
  readonly emptyMessage?: string | undefined;
}

/**
 * AR/AP aging by bucket (سن بدهی), balance in Rial. Severity, not
 * identity — color steps success → warn → danger as buckets age, per the
 * dataviz skill's "meter" semantics, not the categorical fixed order
 * (there is no "series 4" here, only how overdue the money is).
 */
export function AgingBucketsChart({
  buckets,
  ariaLabel,
  height = 260,
  emptyMessage = 'برای مشاهدهٔ سن بدهی، تاریخ مبنا را انتخاب کنید.',
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel,
  amountUnit = 'rial',
}: AgingBucketsChartProps) {
  const hasError = Boolean(error || (status && status >= 400));
  const isEmpty =
    !isLoading &&
    !hasError &&
    (buckets.length === 0 || buckets.every((b) => b.openBalanceRial === 0));
  const containerRef = useEChart(
    (theme) => {
      const severity = (index: number, count: number) => {
        if (count <= 1) return theme.warn;
        const t = index / (count - 1);
        if (t < 0.34) return theme.success;
        if (t < 0.67) return theme.warn;
        return theme.danger;
      };
      return {
        grid: { left: 8, right: 12, top: 20, bottom: 28, containLabel: true },
        xAxis: {
          type: 'category',
          data: buckets.map((b) => b.label),
          axisLine: { lineStyle: { color: theme.grid } },
          axisTick: { show: false },
          axisLabel: { color: theme.textMuted, fontSize: 11 },
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: theme.grid } },
          axisLabel: {
            color: theme.textMuted,
            fontSize: 11,
            formatter: (v: number) =>
              formatChartAmount(v, { unit: amountUnit, includeSuffix: false }),
          },
        },
        tooltip: {
          trigger: 'axis',
          valueFormatter: (v: number) =>
            formatChartAmount(v, { unit: amountUnit, includeSuffix: true }),
          axisPointer: { type: 'shadow' },
        },
        series: [
          {
            type: 'bar',
            barMaxWidth: 40,
            data: buckets.map((b, i) => ({
              value: b.openBalanceRial,
              itemStyle: { color: severity(i, buckets.length), borderRadius: [4, 4, 0, 0] },
            })),
          },
        ],
      };
    },
    [buckets, amountUnit],
  );

  return (
    <ChartFrame
      containerRef={containerRef}
      height={height}
      ariaLabel={ariaLabel}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      error={error}
      status={status}
      errorMessage={errorMessage}
      onRetry={onRetry}
      retryLabel={retryLabel}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
    />
  );
}
